"use client";

/**
 * BrandLoader — a full-page, on-brand loading state for the RARE moments where a
 * brand presence is warranted: a true cold boot or a full-page auth/session
 * check. NOT for in-app, content-area waits — those use the quiet ContentSkeleton
 * (no logo). Keeping the brand mark to genuinely-full-page boots is what stops it
 * reading as "the loader comes back every time".
 *
 * The mark stays still and upright from frame one; only light moves:
 *  - `breathe` (default): a slow opacity pulse. The calmest option — app boot.
 *  - `sunbeam`: the gate curtain / cold-boot splash (founder 08-06). The
 *    porcelain room the app lives in, lit: two warm shafts and a field of gold
 *    dust wake around the mark, whose own glow breathes via a brightened twin.
 *    Replaces the old white `shimmer` sweep, which washed screen-light over the
 *    ink (the notch law: the surface never changes, only the light).
 * Both run on the compositor (opacity / transform) and respect reduced-motion.
 *
 * `size` drives the mark via a CSS var so the gate windows can share ONE
 * pixel-identical frame — same mark, same spot — and the curtain → takeover
 * handoff is invisible.
 */

import type { CSSProperties } from "react";
import { VestraBadge } from "@/components/ui/VestraBadge";

// The dust field: hand-placed motes across three depths (sharp near, blurred
// far). Static data on purpose — the loader must render the same field every
// boot, and the prerendered curtain can't run randomness anyway.
const MOTES = [
  { x: "10%", s: 4, t: 13, d: -2, b: 0.5, o: 0.55 },
  { x: "18%", s: 7, t: 17, d: -9, b: 2.5, o: 0.4 },
  { x: "27%", s: 3, t: 11, d: -5, b: 0, o: 0.6 },
  { x: "36%", s: 5, t: 15, d: -12, b: 1.5, o: 0.45 },
  { x: "44%", s: 3, t: 10, d: -1, b: 0, o: 0.65 },
  { x: "53%", s: 8, t: 19, d: -7, b: 3, o: 0.35 },
  { x: "61%", s: 4, t: 12, d: -4, b: 0.5, o: 0.55 },
  { x: "69%", s: 3, t: 14, d: -10, b: 0, o: 0.5 },
  { x: "77%", s: 6, t: 16, d: -3, b: 2, o: 0.4 },
  { x: "84%", s: 4, t: 11, d: -8, b: 0.5, o: 0.6 },
  { x: "91%", s: 3, t: 13, d: -6, b: 0, o: 0.55 },
  { x: "48%", s: 5, t: 15, d: -11, b: 1.5, o: 0.45 },
];

export function BrandLoader({
  variant = "breathe",
  size = 48,
}: {
  variant?: "breathe" | "sunbeam";
  size?: number;
}) {
  return (
    <div
      className={
        variant === "sunbeam"
          ? "brand-loader brand-loader--sunbeam [--brand-loader-size:48px] min-h-[100vh] flex items-center justify-center bg-bg-base relative overflow-hidden"
          : "brand-loader [--brand-loader-size:48px] min-h-[100vh] flex items-center justify-center bg-bg-base"
      }
      role="status"
      aria-label="Loading"
      style={{ ["--brand-loader-size" as string]: `${size}px` }}
    >
      {variant === "sunbeam" ? (
        <>
          <div
            className="brand-loader__light absolute inset-0 animate-brand-light-in-1 motion-reduce:animate-none motion-reduce:opacity-100"
            aria-hidden="true"
          >
            <div className="brand-loader__beam absolute inset-[-20%] bg-gold-sweep-112 blur-[10px] animate-brand-beam motion-reduce:animate-none" />
            <div className="brand-loader__beam absolute inset-[-20%] bg-brand-loader-gradient blur-[10px] animate-brand-beam motion-reduce:animate-none [animation-delay:-3.5s]" />
            <div className="absolute inset-0 bg-brand-loader-gradient-2 animate-brand-bloom motion-reduce:animate-none" />
          </div>
          <div
            className="brand-loader__dust absolute inset-0 animate-brand-light-in-2 motion-reduce:animate-none motion-reduce:opacity-100"
            aria-hidden="true"
          >
            {MOTES.map((m, i) => (
              <span
                key={i}
                className="brand-loader__mote absolute bottom-[-3%] rounded-full bg-brand-loader-gradient-3 opacity-0 [animation:brand-rise_calc(var(--t)_*_0.6)_linear_infinite] [animation-delay:var(--d)] motion-reduce:animate-none motion-reduce:opacity-0"
                style={
                  {
                    left: m.x,
                    width: m.s * 1.5,
                    height: m.s * 1.5,
                    filter: m.b ? `blur(${m.b}px)` : undefined,
                    // Duration/delay/opacity-peak stay CSS vars (not plain
                    // inline style) because the brand-rise keyframe itself
                    // reads --o at 10%/88%, and the animation must stay a
                    // class so motion-reduce:animate-none can still cancel it
                    // — inline style would out-specificity that override.
                    "--t": `${m.t}s`,
                    "--d": `${m.d}s`,
                    "--o": m.o,
                  } as CSSProperties
                }
              />
            ))}
          </div>
          <div className="brand-loader__stack relative w-(--brand-loader-size) h-(--brand-loader-size)">
            <VestraBadge
              className="brand-loader__mark absolute inset-0 w-(--brand-loader-size) h-(--brand-loader-size) block"
              size={size}
            />
            <VestraBadge
              className="brand-loader__mark brand-loader__mark-lit absolute inset-0 w-(--brand-loader-size) h-(--brand-loader-size) block brightness-[1.16] animate-brand-mark-breathe motion-reduce:animate-none motion-reduce:opacity-50"
              size={size}
            />
          </div>
        </>
      ) : (
        <VestraBadge
          className="brand-loader__breathe w-(--brand-loader-size) h-(--brand-loader-size) block animate-brand-breathe motion-reduce:animate-none"
          size={size}
        />
      )}
    </div>
  );
}
